import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Search, Plus, Edit, Trash2, Users, MessageCircle, AlertTriangle, CheckCircle, Upload, Download, FileDown } from 'lucide-react';

const formatCPF = (cpf) => {
  if (!cpf) return '-';
  if (cpf.length === 14) {
    return cpf.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const ClientList = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [resultDialog, setResultDialog] = useState(false);
  const fileInputRef = useRef(null);
  const admin = typeof isAdmin === 'function' ? isAdmin() : false;

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async (searchTerm = '') => {
    try {
      setLoading(true);
      const response = await clientsAPI.list(searchTerm);
      // Ordenar por nome em ordem alfabética
      const sortedClients = (response.data || []).sort((a, b) => 
        (a.nome_completo || '').localeCompare(b.nome_completo || '', 'pt-BR')
      );
      setClients(sortedClients);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadClients(search);
  };

  const handleDelete = async (id) => {
    try {
      await clientsAPI.delete(id);
      toast.success('Cliente excluído');
      loadClients(search);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir cliente');
    }
  };

  const openWhatsApp = (telefone) => {
    const phone = telefone.replace(/\D/g, '');
    if (phone.length >= 10) {
      const formattedPhone = phone.startsWith('55') ? phone : `55${phone}`;
      window.open(`https://wa.me/${formattedPhone}`, '_blank');
    } else {
      toast.error('Telefone inválido para WhatsApp');
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    try {
      const resp = await clientsAPI.exportCSV();
      const ts = new Date().toISOString().slice(0, 10);
      downloadBlob(resp.data, `clientes_${ts}.csv`);
      toast.success('CSV exportado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao exportar');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const resp = await clientsAPI.importTemplate();
      downloadBlob(resp.data, 'modelo_clientes.csv');
      toast.success('Modelo baixado');
    } catch (error) {
      toast.error('Erro ao baixar modelo');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setImporting(true);
      const resp = await clientsAPI.importCSV(file);
      setImportResult(resp.data);
      setResultDialog(true);
      if (resp.data.criados > 0) toast.success(`${resp.data.criados} cliente(s) importado(s)`);
      if (resp.data.ignorados > 0) toast.warning(`${resp.data.ignorados} linha(s) ignorada(s)`);
      await loadClients(search);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Clientes</h1>
          <p className="text-muted-foreground">Gerencie seus clientes cadastrados</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {admin && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleImportFile}
                className="hidden"
                data-testid="import-csv-file"
              />
              <Button
                variant="outline"
                onClick={handleDownloadTemplate}
                data-testid="download-template-btn"
                title="Baixar modelo CSV"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Modelo
              </Button>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                data-testid="import-csv-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {importing ? 'Importando...' : 'Importar'}
              </Button>
              <Button
                variant="outline"
                onClick={handleExport}
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </>
          )}
          <Button onClick={() => navigate('/clientes/novo')} data-testid="new-client-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Cliente
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Lista de Clientes
            </CardTitle>
            <form onSubmit={handleSearch} className="flex-1 flex gap-2 sm:max-w-md sm:ml-auto">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome, CPF ou CNPJ..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" variant="secondary" data-testid="search-btn">
                Buscar
              </Button>
            </form>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Nome</TableHead>
                    <TableHead className="hidden sm:table-cell">CPF/CNPJ</TableHead>
                    <TableHead className="hidden md:table-cell">Telefone</TableHead>
                    <TableHead className="hidden lg:table-cell">Data Cadastro</TableHead>
                    <TableHead className="hidden lg:table-cell">Cadastrado por</TableHead>
                    <TableHead>Projeto</TableHead>
                    <TableHead className="w-32">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        Nenhum cliente encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    clients.map((client) => (
                      <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                        <TableCell className="font-medium">{client.nome_completo}</TableCell>
                        <TableCell className="hidden sm:table-cell mono text-sm">
                          {formatCPF(client.cpf)}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">{client.telefone || '-'}</TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {formatDate(client.created_at)}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {client.criado_por_nome || '-'}
                        </TableCell>
                        <TableCell>
                          {client.tem_projeto_ativo ? (
                            <Badge variant="secondary" className="gap-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">
                              <CheckCircle className="w-3 h-3" />
                              Ativo
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="gap-1 text-amber-600 dark:text-amber-400 border-amber-500/30">
                              <AlertTriangle className="w-3 h-3" />
                              Sem projeto
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {client.telefone && (
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => openWhatsApp(client.telefone)}
                                data-testid={`whatsapp-client-${client.id}`}
                                title="Abrir WhatsApp"
                              >
                                <MessageCircle className="w-4 h-4 text-green-600" />
                              </Button>
                            )}
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => navigate(`/clientes/${client.id}`)}
                              data-testid={`edit-client-${client.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="text-destructive"
                                  data-testid={`delete-client-${client.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Excluir Cliente</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Tem certeza que deseja excluir o cliente "{client.nome_completo}"?
                                    Esta ação não pode ser desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDelete(client.id)}>
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Resultado da Importação */}
      <Dialog open={resultDialog} onOpenChange={setResultDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Resultado da Importação</DialogTitle>
            <DialogDescription>
              Resumo dos registros processados.
            </DialogDescription>
          </DialogHeader>
          {importResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg border bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200">
                  <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium">Criados</p>
                  <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400" data-testid="import-criados">
                    {importResult.criados}
                  </p>
                </div>
                <div className="p-3 rounded-lg border bg-amber-50 dark:bg-amber-900/20 border-amber-200">
                  <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">Ignorados</p>
                  <p className="text-2xl font-bold text-amber-700 dark:text-amber-400" data-testid="import-ignorados">
                    {importResult.ignorados}
                  </p>
                </div>
              </div>
              {importResult.erros && importResult.erros.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">
                    Erros ({importResult.total_erros || importResult.erros.length})
                    {importResult.total_erros > importResult.erros.length && (
                      <span className="text-muted-foreground font-normal"> — mostrando os 50 primeiros</span>
                    )}
                  </p>
                  <ScrollArea className="h-48 border rounded-md">
                    <div className="p-2 space-y-1">
                      {importResult.erros.map((e, i) => (
                        <div key={i} className="text-xs p-2 rounded bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30">
                          <span className="font-mono text-red-700 dark:text-red-400">Linha {e.linha}:</span>{' '}
                          <span className="text-red-900 dark:text-red-300">{e.erro}</span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setResultDialog(false)} data-testid="close-import-result">
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientList;
